import React, { ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getResidentialUnit } from '@/services/residential-units';
import { Trans } from '@lingui/react/macro';
import { Breadcrumbs, LoadingIndicator, ResidentialUnitFloorPlan } from '@ygtechnologies/bygr-frontend-component-library';
import { Tabs } from '@ygtechnologies/rubus-ui';
import { Link } from 'react-router-dom';
import ResidentialUnitDocuments from '@/seller/components/residential-units/ResidentialUnitDetails/ResidentialUnitDocuments';
import ResidentialUnitOrders from '@/seller/components/residential-units/ResidentialUnitDetails/ResidentialUnitOrders';
import ResidentialUnitChangeRequests from '@/seller/components/residential-units/ResidentialUnitDetails/ResidentialUnitChangeRequests';
import theme from './ResidentialUnitDetails.module.scss';
import { TranslatableText, ResidentialUnit, Project, Profile } from '@/types';
import { useAsyncState, usePageTitle } from '@/utils/hooks';
import { IState } from '@/state/IState';
import ResidentialUnitBuyerInfo from '../ResidentialUnitBuyerInfo/ResidentialUnitBuyerInfo';
import ResidentialUnitContextMenu from '../ResidentialUnitContextMenu';
import ResidentialUnitExternalAgreements from '@/seller/components/residential-units/ResidentialUnitDetails/ResidentialUnitExternalAgreements';
import { PageTitle, SimplePageTitle } from '@/components/layout/PageTitle';
import ResidentialUnitDetailsQuickNav from './ResidentialUnitDetailsQuickNav';
import { classnames } from '@/utils/html';
import { ResidentialUnitDashboard } from './ResidentialUnitDashboard';
import CommentsFilterable from '@/seller/components/comments/CommentsFilterable';
import ResidentialUnitHistoryLog from './ResidentialUnitHistoryLog';
import { OptionConfigurator, SellerOptionConfigurator } from '@/components/OptionConfigurator';
import { BreadcrumbsProjectLink } from '@/components/navigation';

export type ResidentialUnitDetailsTab =
    | 'dashboard'
    | 'documents'
    | 'changes'
    | 'external-agreements'
    | 'orders'
    | 'comments'
    | 'internal-comments'
    | 'history-log'
    | 'floor-plan'
    | 'options';

interface TabSettings {
    id: ResidentialUnitDetailsTab;
    name: TranslatableText;
    shouldRender?(project: Project): boolean;
    render(opts: { residentialUnit: ResidentialUnit; profile: Profile; project: Project }): ReactNode;
}

const tabs: TabSettings[] = [
    {
        id: 'dashboard',
        name: <Trans>Dashboard</Trans>,
        render: ({ residentialUnit }) => <ResidentialUnitDashboard residentialUnitId={residentialUnit.id} />,
    },
    {
        id: 'documents',
        name: <Trans>Documents</Trans>,
        render: ({ residentialUnit }) => <ResidentialUnitDocuments residentialUnitId={residentialUnit.id} />,
    },
    {
        id: 'options',
        name: <Trans>Options</Trans>,
        render: ({ residentialUnit, project }) => <SellerOptionConfigurator residentialUnitId={residentialUnit.id} />,
    },
    {
        id: 'changes',
        name: <Trans>Change requests</Trans>,
        render: ({ residentialUnit }) => <ResidentialUnitChangeRequests residentialUnitId={residentialUnit.id} />,
    },
    {
        id: 'external-agreements',
        name: <Trans>External agreements</Trans>,
        render: ({ residentialUnit }) => <ResidentialUnitExternalAgreements residentialUnitId={residentialUnit.id} />,
    },
    {
        id: 'orders',
        name: <Trans>Orders</Trans>,
        render: ({ residentialUnit }) => (
            <ResidentialUnitOrders projectId={residentialUnit.projectId} residentialUnitId={residentialUnit.id} />
        ),
    },
    {
        id: 'comments',
        name: <Trans>Messages</Trans>,
        render: ({ residentialUnit, profile, project }) => (
            <CommentsFilterable residentialUnitId={residentialUnit.id} profileId={profile.id} projectId={project.id} />
        ),
    },
    {
        id: 'floor-plan',
        name: <Trans>Floor plan</Trans>,
        render: ({ residentialUnit, project }) => <ResidentialUnitFloorPlan residentialUnitId={residentialUnit.id} />,
    },
    {
        id: 'history-log',
        name: <Trans>History log</Trans>,
        render: ({ residentialUnit, project }) => (
            <ResidentialUnitHistoryLog residentialUnitId={residentialUnit.id} projectId={project.id} />
        ),
    },
];

interface ResidentialUnitDetailsProps {
    residentialUnitId: number;
    tab: ResidentialUnitDetailsTab;
}

const ResidentialUnitDetails: React.FC<ResidentialUnitDetailsProps> = ({ residentialUnitId, tab }) => {
    const profile = useSelector((state: IState) => state.global.activeProfile!);
    const project = useSelector((state: IState) => state.global.activeProject!);

    const [residentialUnit, , reloadUnit] = useAsyncState(
        () => getResidentialUnit(residentialUnitId),
        [residentialUnitId],
        null
    );

    const commentThreads = useSelector((state: IState) => state.seller.projectOverview.comments);
    const tabNotifications = useMemo(
        () => ({
            comments: commentThreads?.filter((x) => x.residentialUnitId === +residentialUnitId && x.unreadMessages > 0)
                .length,
        }),
        [commentThreads, residentialUnitId]
    );
    if (!residentialUnit) {
        return <LoadingIndicator />;
    }

    // Legacy options scrolls at the page level; `fit-height` would cap it and break the sticky tabs.
    const optionsFullHeight = tab === 'options' && !project.useLegacyOptionsView;
    const fullHeight =
        tab === 'comments' || tab === 'internal-comments' || tab === 'dashboard' || optionsFullHeight;

    return (
        <>
            <SimplePageTitle title={residentialUnit.name} />
            <div className={theme.topBar}>
                <div className={theme.navigation}>
                    <Breadcrumbs>
                        <BreadcrumbsProjectLink to="/residential-units">
                            <Trans>All units</Trans>
                        </BreadcrumbsProjectLink>

                        <Breadcrumbs.Item>
                            <span>{residentialUnit.name}</span>
                        </Breadcrumbs.Item>
                    </Breadcrumbs>

                    <ResidentialUnitDetailsQuickNav
                        currentResidentialUnitId={residentialUnit.id}
                        projectId={residentialUnit.projectId}
                    />
                </div>

                <div className={theme.actions}>
                    <ResidentialUnitBuyerInfo residentialUnit={residentialUnit} />
                    <ResidentialUnitContextMenu residentialUnit={residentialUnit} onUpdate={() => reloadUnit()} />
                </div>
            </div>

            <Tabs.Container activeId={tab} variant="underline" sticky>
                {tabs
                    .filter((x) => (x.shouldRender ? x.shouldRender(project) : true))
                    .map((t) => (
                        <Tabs.Tab
                            key={t.id}
                            id={t.id}
                            as={Link}
                            label={t.name}
                            to={`/projects/${project.id}/residential-units/${residentialUnit.id}/${t.id}`}
                            badge={tabNotifications[t.id as keyof typeof tabNotifications] || undefined}
                        />
                    ))}
                <Tabs.Content>
                    <div className={classnames({ 'fit-height': fullHeight })}>
                        {tabs.find((t) => t.id === tab)?.render({ residentialUnit, profile, project })}
                    </div>
                </Tabs.Content>
            </Tabs.Container>
        </>
    );
};

export default ResidentialUnitDetails;
